import GiftCard from "./GiftCard"
import { useState } from "react"
import { errorMessage, type OwnedItem } from "../types"
import ItemImage from "./ItemImage"

export default function OwnedItemRow({
  item,
  onEdit,
  editingDisabled = false,
  onDelete,
}: {
  item: OwnedItem
  editingDisabled?: boolean
  onEdit: () => void
  onDelete: () => Promise<void>
}) {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleDelete() {
    if (deleting) return
    setDeleting(true)
    setError(null)
    try {
      await onDelete()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setDeleting(false)
    }
  }

  return (
    <GiftCard id={item.id} name={item.name}>
      <div className="sm:row-span-2">
        <ItemImage src={item.image_url} size="card" />
      </div>
      <div className="min-w-0 break-words">
        <h3 className="text-base font-medium text-gray-900">{item.name}</h3>

        {item.notes && (
          <p className="text-base text-gray-600 mt-0.5 whitespace-pre-wrap break-words">{item.notes}</p>
        )}
        {error && (
          <p role="alert" className="min-h-11 min-w-11 px-3 inline-flex items-center text-sm text-red-600 mt-2">
            {error}
          </p>
        )}
      </div>
      <div className="col-span-2 flex flex-wrap items-center gap-x-2 sm:col-start-2 sm:row-start-2 sm:col-span-1">
        {item.url && (
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="min-h-11 min-w-11 px-3 inline-flex items-center text-sm text-blue-600 hover:underline py-3"
          >
            View at store ↗
          </a>
        )}

        {confirmDelete ? (
          <>
            <span className="min-h-11 min-w-11 px-3 inline-flex items-center text-sm text-gray-500">Remove this gift from your list?</span>
            <button
              onClick={() => {
                void handleDelete()
              }}
              disabled={deleting}
              className="min-h-11 min-w-11 px-3 inline-flex items-center text-sm text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
            >
              {deleting ? "Removing..." : "Remove gift"}
            </button>
            <button
              onClick={() => {
                setConfirmDelete(false)
                setError(null)
              }}
              disabled={deleting}
              className="min-h-11 min-w-11 px-3 inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              id={`edit-${item.id}`}
              onClick={onEdit}
              disabled={editingDisabled}
              className="min-h-11 min-w-11 px-3 inline-flex items-center text-sm text-gray-500 hover:text-gray-900 transition-colors"
            >
              Edit
            </button>
            <button
              onClick={() => setConfirmDelete(true)}
              className="min-h-11 min-w-11 px-3 inline-flex items-center text-sm text-gray-500 hover:text-red-600 transition-colors"
            >
              Remove
            </button>
          </>
        )}
      </div>
    </GiftCard>
  )
}
